import React, { Fragment, useState } from 'react'
import { ProductsSectionProps, BrandProductsExpandableEntities } from './ProductsSection.types'
import styles from './ProductsSection.module.scss'
import cl from 'classnames'
import { inject } from 'mobx-react'
import { BRAND_PRODUCTS_STORE, BrandProductsRangeProps, BrandProductsSortProps, ROUTING_STORE } from 'store'
import { observer } from 'mobx-react-lite'
import {
  H2, ImageSquare, NoneLabel, SectionContainer,
  TableBodyContainer, TableBodyLine, TableContainer,
  TableExpandableCell, TableHeader, Text1,
  TableSliderFilter,
  TablePeriodFilter,
  PaginationButtons,
  TableTagsCell,
  TableCellWithAdditionalCount,
  TableCellAgreementBudget,
  TableCellAgreementPeriod,
  Button
} from 'components/shared'
import { getMainImageSrcForObj, getTableHeight, useGetScrollWidth } from 'utilities'
import tableBodyContainerStyle from '../../../../components/shared/TableBodyContainer/TableBodyContainer.module.scss'

// const brandProductsTableWidths = {
//   name: 22,
//   projects: 17,
//   agreements: 16,
//   budget: 15,
//   period: 11,
//   tags: 19
// }
const brandProductsTableWidths = {
  name: 382,
  projects: 300,
  agreements: 280,
  budget: 250,
  period: 200,
  tags: 340
}

const ProductsSectionComponent = (props: ProductsSectionProps) => {
  const {
    [BRAND_PRODUCTS_STORE]: {
      hasProducts,
      productsTotalCount,
      searchProps,
      onSearchPropsChange,
      // handleSearchPropsChange,
      searchResults: products,
      filterRanges,
      paginationInfo,
      onPageSelect,
      agreementPeriodFilter,
      isColumnMarked,
      getSortDirection,
      handleSortDirectionChange,
      handleSyncProducts,
      ["rootStore"] : {
        BRAND_STORE : {
          brandToView
        }
      },
      getSearchedProducts
    },
    [ROUTING_STORE]: {
      push
    }
  } = props

  const tableHeight = getTableHeight(productsTotalCount + 2)

  const [expandedProducts, setExpandedProducts] =
    useState<{ [key: number]: BrandProductsExpandableEntities }>({})

  const onExpandClick = (productId: number, entity: BrandProductsExpandableEntities) => () => {
    const isExpanded = expandedProducts[productId] === entity
    const newValue = isExpanded ? null : entity
    setExpandedProducts(s => ({
      ...s,
      [productId]: newValue
    }))
  }

  const { paginationStyle, handleScroll } = useGetScrollWidth()
  const scrollbarSpacing = 4.8

  return (
    <SectionContainer className={cl(styles.container)} extraPaddingBottom={hasProducts}>
      <div className={styles.header}>
        <H2>Products ({productsTotalCount})</H2>
      {  
      brandToView.shopifyAccessToken && brandToView.shopifyStoreUrl && 
        <Button
          preset='primary'
          style={{ width: 250 }}
          onClick={async()=>{
            await handleSyncProducts();
            await getSearchedProducts();
          }}
          >
          Sync Products From Shopify Store
        </Button>
      }
      </div>

      {!hasProducts
        ? <NoneLabel />
        : (
          <TableContainer
            noBoxShadow
            style={{
              marginTop: 20,
              maxHeight: tableHeight,
              minHeight: 250,
              height: tableHeight,
              overflowX: 'auto'
            }}
            className={styles.thinScroll}
            onScroll={handleScroll}
          >
            <TableHeader
              tableColumns={[
                {
                  label: 'Products',
                  // widthPercentage: brandProductsTableWidths.name,
                  widthPx: brandProductsTableWidths.name + scrollbarSpacing,
                  sortDirection: getSortDirection(BrandProductsSortProps.NAME),
                  setSortDirection: handleSortDirectionChange(BrandProductsSortProps.NAME),
                  isColumnMarked: isColumnMarked(BrandProductsSortProps.NAME),
                  inputProps: {
                    placeholder: 'Search...',
                    value: searchProps.productName,
                    onChange: productName => onSearchPropsChange({ productName })
                  }
                },
                {
                  label: 'Projects',
                  // widthPercentage: brandProductsTableWidths.projects,
                  widthPx: brandProductsTableWidths.projects,
                  inputProps: {
                    placeholder: 'Search...',
                    value: searchProps.projectName,
                    onChange: projectName => onSearchPropsChange({ projectName })
                  }
                },
                {
                  label: 'Active agreements',
                  // widthPercentage: brandProductsTableWidths.agreements,
                  widthPx: brandProductsTableWidths.agreements,
                  inputProps: {
                    placeholder: 'Search...',
                    value: searchProps.agreementName,
                    onChange: agreementName => onSearchPropsChange({ agreementName })
                  }
                },
                {
                  label: 'Agreement budget remaining',
                  // widthPercentage: brandProductsTableWidths.budget,
                  widthPx: brandProductsTableWidths.budget,
                  filterComponent: (
                    <TableSliderFilter
                      label='Budget'
                      min={
                        filterRanges
                          ? filterRanges[BrandProductsRangeProps.AGREEMENT_BUDGET].min
                          : null
                      }
                      max={
                        filterRanges
                          ? filterRanges[BrandProductsRangeProps.AGREEMENT_BUDGET].max
                          : null
                      }
                      onChange={
                        ([minAgreementBudget, maxAgreementBudget]) =>
                          onSearchPropsChange({ minAgreementBudget, maxAgreementBudget })
                      }
                    />
                  )
                },
                {
                  label: 'Agreement period',
                  // widthPercentage: brandProductsTableWidths.period,
                  widthPx: brandProductsTableWidths.period,
                  filterComponent: (
                    <TablePeriodFilter
                      period={agreementPeriodFilter}
                      setPeriod={(period) => {
                        if (period === null) {
                          onSearchPropsChange({
                            agreementStart: null,
                            agreementEnd: null
                          })
                        } else {
                          onSearchPropsChange({
                            agreementStart: period.start.format('YYYY-MM-DD'),
                            agreementEnd: period.end.format('YYYY-MM-DD')
                          })
                        }
                      }}
                    />
                  )
                },
                {
                  label: 'Tags',
                  // widthPercentage: brandProductsTableWidths.tags,
                  widthPx: brandProductsTableWidths.tags,
                  sortDirection: getSortDirection(BrandProductsSortProps.TAGS),
                  setSortDirection: handleSortDirectionChange(BrandProductsSortProps.TAGS),
                  isColumnMarked: isColumnMarked(BrandProductsSortProps.TAGS),
                  inputProps: {
                    placeholder: 'Search...',
                    value: searchProps.tags,
                    onChange: tags => onSearchPropsChange({ tags })
                  }
                }
              ]}
            />
            <TableBodyContainer className={tableBodyContainerStyle.tableBodyContainer}>
              {products.map(product => {
                const isExpanded = !!expandedProducts[product.id]

                const renderExpandedProjectsLines = () => {
                  const entries = Array.from(product.projects.entries())

                  return (
                    entries.map(([projectName, agreements], index) => {
                      const isLast = index === entries.length - 1

                      const agreementNames = Array.from(agreements.values())
                      const agreementsList = agreementNames.map(name => product.agreementDTOMap.get(name))

                      return (
                        <TableBodyLine
                          key={index}
                          cells={[
                            {
                              // widthPercentage: brandProductsTableWidths.name
                              widthPx: brandProductsTableWidths.name
                            },
                            {
                              // widthPercentage: brandProductsTableWidths.projects,
                              widthPx: brandProductsTableWidths.projects,
                              className: cl(styles.expandedLeft, {
                                [styles.expandedBottom]: isLast
                              }),
                              children: <Text1 overflowEllipsis>{projectName}</Text1>
                            },
                            {
                              // widthPercentage: brandProductsTableWidths.agreements,
                              widthPx: brandProductsTableWidths.agreements,
                              className: cl(styles.expandedMiddle, {
                                [styles.expandedBottom]: isLast
                              }),
                              children: (
                                <TableCellWithAdditionalCount
                                  text={Array.from(agreements.values())[0]}
                                  totalCount={agreements.size}
                                />
                              )
                            },
                            {
                              // widthPercentage: brandProductsTableWidths.budget,
                              widthPx: brandProductsTableWidths.budget,
                              className: cl(styles.expandedMiddle, {
                                [styles.expandedBottom]: isLast
                              }),
                              children: (
                                <TableCellAgreementBudget
                                  data={agreementsList}
                                />
                              )
                            },
                            {
                              // widthPercentage: brandProductsTableWidths.period,
                              widthPx: brandProductsTableWidths.period,
                              className: cl(styles.expandedMiddle, {
                                [styles.expandedBottom]: isLast
                              }),
                              children: (
                                <TableCellAgreementPeriod
                                  data={agreementsList}
                                />
                              )
                            },
                            {
                              // widthPercentage: brandProductsTableWidths.tags,
                              widthPx: brandProductsTableWidths.tags,
                              className: cl(styles.expandedRight, {
                                [styles.expandedBottom]: isLast
                              })
                            }
                          ]}
                        />
                      )
                    })
                  )
                }
                const renderExpandedAgreementsLines = () => {
                  const entries = Array.from(product.agreements.entries())

                  return (
                    entries.map(([agreementName, projects], index) => {
                      const isLast = index === entries.length - 1
                      const agreement = product.agreementDTOMap.get(agreementName)

                      return (
                        <TableBodyLine
                          key={index}
                          cells={[
                            {
                              // widthPercentage: brandProductsTableWidths.name
                              widthPx: brandProductsTableWidths.name
                            },
                            {
                              // widthPercentage: brandProductsTableWidths.projects,
                              widthPx: brandProductsTableWidths.projects,
                              className: cl(styles.expandedLeft, {
                                [styles.expandedBottom]: isLast
                              }),
                              children: (
                                <TableCellWithAdditionalCount
                                  text={Array.from(projects.values())[0]}
                                  totalCount={projects.size}
                                />
                              )
                            },
                            {
                              // widthPercentage: brandProductsTableWidths.agreements,
                              widthPx: brandProductsTableWidths.agreements,
                              className: cl(styles.expandedMiddle, {
                                [styles.expandedBottom]: isLast
                              }),
                              children: <Text1 overflowEllipsis>{agreementName}</Text1>
                            },
                            {
                              // widthPercentage: brandProductsTableWidths.budget,
                              widthPx: brandProductsTableWidths.budget,
                              className: cl(styles.expandedMiddle, {
                                [styles.expandedBottom]: isLast
                              }),
                              children: (
                                <TableCellAgreementBudget
                                  data={agreement}
                                />
                              )
                            },
                            {
                              // widthPercentage: brandProductsTableWidths.period,
                              widthPx: brandProductsTableWidths.period,
                              className: cl(styles.expandedMiddle, {
                                [styles.expandedBottom]: isLast
                              }),
                              children: (
                                <TableCellAgreementPeriod
                                  data={agreement}
                                />
                              )
                            },
                            {
                              // widthPercentage: brandProductsTableWidths.tags,
                              widthPx: brandProductsTableWidths.tags,
                              className: cl(styles.expandedRight, {
                                [styles.expandedBottom]: isLast
                              })
                            }
                          ]}
                        />
                      )
                    })
                  )
                }

                const renderExpandedLines = () => {
                  if (!isExpanded) return null

                  return expandedProducts[product.id] ===
                    BrandProductsExpandableEntities.PROJECT
                    ? renderExpandedProjectsLines()
                    : renderExpandedAgreementsLines()
                }

                return (
                  <Fragment key={product.id}>
                    <TableBodyLine
                      cells={[
                        {
                          // widthPercentage: brandProductsTableWidths.name,
                          widthPx: brandProductsTableWidths.name,
                          isColumnMarked: isColumnMarked(BrandProductsSortProps.NAME),
                          onClick: () => push(`/products/edit/${product.id}`),
                          children: (
                            <>
                              <ImageSquare
                                src={getMainImageSrcForObj(product)}
                                width={30}
                              />
                              <Text1 className={styles.productName} title={product.name}>
                                {product.name}
                              </Text1>
                            </>
                          )
                        },
                        {
                          // widthPercentage: brandProductsTableWidths.projects,
                          widthPx: brandProductsTableWidths.projects,
                          className: cl({ [styles.expandedTopLeft]: isExpanded }),
                          children: (
                            <TableExpandableCell
                              entityName={BrandProductsExpandableEntities.PROJECT}
                              objects={Array.from(product.projects.keys())}
                              onClick={onExpandClick(
                                product.id, BrandProductsExpandableEntities.PROJECT
                              )}
                              isExpanded={
                                expandedProducts[product.id] ===
                                BrandProductsExpandableEntities.PROJECT
                              }
                            />
                          )
                        },
                        {
                          // widthPercentage: brandProductsTableWidths.agreements,
                          widthPx: brandProductsTableWidths.agreements,
                          className: cl({ [styles.expandedTop]: isExpanded }),
                          children: (
                            <TableExpandableCell
                              entityName={BrandProductsExpandableEntities.AGREEMENT}
                              objects={Array.from(product.agreements.keys())}
                              onClick={onExpandClick(
                                product.id, BrandProductsExpandableEntities.AGREEMENT
                              )}
                              isExpanded={
                                expandedProducts[product.id] ===
                                BrandProductsExpandableEntities.AGREEMENT
                              }
                            />
                          )
                        },
                        {
                          // widthPercentage: brandProductsTableWidths.budget,
                          widthPx: brandProductsTableWidths.budget,
                          className: cl({ [styles.expandedTop]: isExpanded }),
                          children: (
                            <TableCellAgreementBudget
                              data={Array.from(product.agreementDTOMap.values())}
                            />
                          )
                        },
                        {
                          // widthPercentage: brandProductsTableWidths.period,
                          widthPx: brandProductsTableWidths.period,
                          className: cl({ [styles.expandedTop]: isExpanded }),
                          children: (
                            <TableCellAgreementPeriod
                              data={Array.from(product.agreementDTOMap.values())}
                            />
                          )
                        },
                        {
                          // widthPercentage: brandProductsTableWidths.tags,
                          widthPx: brandProductsTableWidths.tags,
                          className: cl({ [styles.expandedTopRight]: isExpanded }),
                          isColumnMarked: isColumnMarked(BrandProductsSortProps.TAGS),
                          children: <TableTagsCell tags={product.tags} />
                        }
                      ]}
                    />
                    {renderExpandedLines()}
                  </Fragment>
                )
              })}
            </TableBodyContainer>
            <PaginationButtons
              {...paginationInfo}
              onPageSelect={onPageSelect}
              style={paginationStyle}
            />
          </TableContainer>
        )}

    </SectionContainer>
  )
}

const ProductsSection = inject(
  BRAND_PRODUCTS_STORE,
  ROUTING_STORE
)(observer(ProductsSectionComponent))
export { ProductsSection }
